import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

const slides = [
  {
    id: 1,
    badge: '真正能做事的 AI 智能体框架',
    title: 'OpenClaw',
    subtitle: '让 AI 真正帮你做事',
    desc: '清理收件箱、发送邮件、管理日历。支持企业微信、飞书、钉钉与微信生态对话接入，打造属于你的 AI 助理。',
    image: 'https://public.readdy.ai/ai/img_res/2870c354b2f0a41724a9b7c8bc3d8c81.jpg',
    accent: 'from-cyan-500 to-teal-500',
    tag: 'ri-robot-2-line',
    primaryLink: '/quick-start',
    primaryText: '快速开始',
    secondaryLink: '/installation',
    secondaryText: '安装向导',
  },
  {
    id: 2,
    badge: '多渠道消息接入',
    title: '连接一切对话渠道',
    subtitle: '企业微信 · 飞书 · 钉钉 · 微信',
    desc: '一套框架，接入所有主流企业通讯平台。无论用户在哪里，OpenClaw 都能让 AI 智能体随时响应。',
    image: 'https://readdy.ai/api/search-image?query=multiple%20chat%20messaging%20platforms%20connected%20network%20nodes%20glowing%20lines%20dark%20background%20WeChat%20DingTalk%20Feishu%20enterprise%20communication%20digital%20connection%20abstract%20technology%20neon%20blue%20teal%20light%20rays%20futuristic%20concept%20art%20cinematic&width=1440&height=800&seq=hero2&orientation=landscape',
    accent: 'from-teal-500 to-cyan-400',
    tag: 'ri-message-3-line',
    primaryLink: '/channels',
    primaryText: '查看渠道',
    secondaryLink: '/quick-start',
    secondaryText: '立即体验',
  },
  {
    id: 3,
    badge: '支持主流 AI 模型',
    title: '接入顶级 AI 大模型',
    subtitle: 'GPT · Claude · 通义 · 智谱 · Moonshot',
    desc: '兼容 OpenAI、Anthropic、智谱 GLM、通义千问、Moonshot 等国内外主流大模型，灵活切换，按需使用。',
    image: 'https://readdy.ai/api/search-image?query=AI%20large%20language%20models%20neural%20network%20brain%20glowing%20nodes%20connections%20dark%20background%20abstract%20digital%20intelligence%20cyan%20purple%20light%20particles%20floating%20data%20streams%20futuristic%20technology%20concept%20art%20ultra%20detailed%20cinematic%20lighting&width=1440&height=800&seq=hero3&orientation=landscape',
    accent: 'from-violet-500 to-cyan-500',
    tag: 'ri-brain-line',
    primaryLink: '/models',
    primaryText: '查看模型',
    secondaryLink: '/quick-start',
    secondaryText: '快速开始',
  },
  {
    id: 4,
    badge: '跨平台部署',
    title: '随处部署，随时运行',
    subtitle: 'macOS · Linux · Windows · Docker · 云端',
    desc: '支持桌面、服务器、容器、云平台及边缘设备全场景部署。一次配置，多端运行，让 AI 智能体无处不在。',
    image: 'https://readdy.ai/api/search-image?query=cross%20platform%20deployment%20server%20cloud%20computing%20docker%20containers%20glowing%20infrastructure%20dark%20background%20macOS%20Linux%20Windows%20abstract%20technology%20neon%20green%20cyan%20light%20digital%20network%20nodes%20futuristic%20concept%20art%20cinematic%20ultra%20detailed&width=1440&height=800&seq=hero4&orientation=landscape',
    accent: 'from-emerald-500 to-cyan-500',
    tag: 'ri-server-line',
    primaryLink: '/platform',
    primaryText: '查看平台',
    secondaryLink: '/installation',
    secondaryText: '安装向导',
  },
];

export default function Hero() {
  const [current, setCurrent] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const goTo = useCallback((index: number) => {
    if (animating) return;
    setAnimating(true);
    setCurrent(index);
    setTimeout(() => setAnimating(false), 600);
  }, [animating]);

  const prev = useCallback(() => {
    goTo((current - 1 + slides.length) % slides.length);
  }, [current, goTo]);

  const next = useCallback(() => {
    goTo((current + 1) % slides.length);
  }, [current, goTo]);

  useEffect(() => {
    const interval = setInterval(() => {
      next();
    }, 5000);
    return () => clearInterval(interval);
  }, [next]);

  const slide = slides[current];

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Images */}
      {slides.map((s, i) => (
        <div
          key={s.id}
          className="absolute inset-0 z-0 transition-opacity duration-700"
          style={{ opacity: i === current ? 1 : 0 }}
        >
          <img
            src={s.image}
            alt={s.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-slate-900/80 to-slate-900"></div>
        </div>
      ))}

      {/* Hero Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 py-24 sm:py-32">
        <div className="max-w-4xl mx-auto text-center">
          <div
            key={`badge-${current}`}
            className={`inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 backdrop-blur-md rounded-full text-cyan-400 mb-6 border border-cyan-400/30 transition-all duration-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <i className={`${slide.tag} text-cyan-400`}></i>
            <span className="text-xs sm:text-sm font-semibold">{slide.badge}</span>
          </div>

          <h1
            key={`title-${current}`}
            className={`text-4xl sm:text-5xl md:text-7xl font-extrabold text-white leading-tight mb-3 transition-all duration-700 delay-100 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            {slide.title}
          </h1>

          <p
            key={`sub-${current}`}
            className={`text-base sm:text-xl md:text-2xl text-cyan-400 font-semibold mb-4 transition-all duration-700 delay-200 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            {slide.subtitle}
          </p>

          <p
            key={`desc-${current}`}
            className={`text-sm sm:text-base md:text-xl text-gray-300 leading-relaxed mb-8 sm:mb-12 max-w-3xl mx-auto transition-all duration-700 delay-300 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            {slide.desc}
          </p>

          <div className={`flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-5 transition-all duration-700 delay-400 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <Link
              to={slide.primaryLink}
              className={`w-full sm:w-auto group px-7 py-3.5 bg-gradient-to-r ${slide.accent} text-white rounded-lg transition-all shadow-xl hover:shadow-2xl whitespace-nowrap cursor-pointer flex items-center justify-center gap-3 font-semibold text-sm hover:scale-105`}
            >
              {slide.primaryText}
              <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
            </Link>
            <Link
              to={slide.secondaryLink}
              className="w-full sm:w-auto px-7 py-3.5 bg-slate-800 text-cyan-400 rounded-lg hover:bg-slate-700 transition-all whitespace-nowrap cursor-pointer font-semibold text-sm border border-cyan-500/30 flex items-center justify-center gap-2 hover:scale-105"
            >
              {slide.secondaryText}
            </Link>
            <a
              href="https://github.com/openclaw/openclaw"
              target="_blank"
              rel="noreferrer nofollow"
              className="w-full sm:w-auto px-7 py-3.5 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-all whitespace-nowrap cursor-pointer font-semibold text-sm border border-slate-700 flex items-center justify-center gap-2 hover:scale-105"
            >
              <i className="ri-github-fill text-lg"></i>
              GitHub
            </a>
          </div>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mt-16 sm:mt-24 max-w-5xl mx-auto">
          {[
            { value: '10K+', label: 'GitHub Stars', icon: 'ri-star-line' },
            { value: '200+', label: '贡献者', icon: 'ri-team-line' },
            { value: '50K+', label: '下载量', icon: 'ri-download-line' },
            { value: '15+', label: '支持渠道', icon: 'ri-message-3-line' },
          ].map((stat, index) => (
            <div
              key={stat.label}
              className={`text-center bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-md rounded-xl p-4 sm:p-6 border border-cyan-400/20 hover:border-cyan-400/40 transition-all duration-700 hover:scale-105 hover:shadow-xl hover:shadow-cyan-500/20 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${500 + index * 100}ms` }}
            >
              <div className="flex items-center justify-center mb-2 sm:mb-3">
                <i className={`${stat.icon} text-2xl sm:text-3xl text-cyan-400`}></i>
              </div>
              <div className="text-2xl sm:text-4xl font-bold text-white mb-1 sm:mb-2">{stat.value}</div>
              <div className="text-gray-400 font-medium text-xs sm:text-sm">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Arrow Controls - 手机端缩小并移到底部附近避免遮挡 */}
      <button
        onClick={prev}
        className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 z-20 w-9 h-9 sm:w-12 sm:h-12 flex items-center justify-center bg-slate-800/70 hover:bg-slate-700 border border-cyan-500/30 rounded-full text-cyan-400 transition-all hover:scale-110 cursor-pointer"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-xl sm:text-2xl"></i>
      </button>
      <button
        onClick={next}
        className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 z-20 w-9 h-9 sm:w-12 sm:h-12 flex items-center justify-center bg-slate-800/70 hover:bg-slate-700 border border-cyan-500/30 rounded-full text-cyan-400 transition-all hover:scale-110 cursor-pointer"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-xl sm:text-2xl"></i>
      </button>

      {/* Dot Indicators */}
      <div className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 sm:gap-3">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`transition-all duration-300 rounded-full cursor-pointer ${i === current ? 'w-6 sm:w-8 h-2 sm:h-2.5 bg-cyan-400' : 'w-2 sm:w-2.5 h-2 sm:h-2.5 bg-slate-600 hover:bg-slate-400'}`}
            aria-label={`跳转到第${i + 1}张`}
          />
        ))}
      </div>
    </div>
  );
}
